import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { create } from "zustand";
import { formatDistanceToNow } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetTitle,
  cn,
} from "@myela/ui";
import {
  ArrowLeft,
  Bell,
  Check,
  CheckCheck,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Contact,
  CreditCard,
  DollarSign,
  FileSignature,
  FileText,
  FolderOpen,
  Gift,
  HelpCircle,
  House,
  LayoutGrid,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  Moon,
  Package,
  PhoneCall,
  Plug,
  ScrollText,
  Settings,
  Sparkles,
  Store,
  Sun,
  UserRound,
  Users,
  UsersRound,
  Wrench,
  ArrowLeftRight,
  type LucideIcon,
} from "lucide-react";
import { Lockup, MyelaMark } from "@/components/brand/Lockup";
import { useTheme } from "@/components/theme-provider";
import { useAuth } from "@/hooks/useAuth";
import { useInternalUi } from "@/hooks/useInternalUi";
import { toast } from "@/hooks/use-toast";
import {
  useMarkAlertRead,
  useMarkAllAlertsRead,
  useUnreadAlertCount,
  useUserAlerts,
  type UserAlert,
} from "@/hooks/useUserAlerts";
import { buildAlertLink } from "@/lib/alertLink";
import { productLabel } from "@/lib/brand";
import { AGENT_STATEMENTS_ENABLED, canReadCommissions } from "@/lib/commissionAccess";
import { dashboardUrl } from "@/lib/dashboard";

// The left menu, as the Dashboard draws it (payments-dashboard components/shell/PrimaryNav.tsx):
// a 70px icon rail that opens into a panel on hover, or stays open when docked
// from the tab on its edge. The Myela lockup and the bell head it: inside a
// product the lockup is the product switcher, so the menu below holds only the
// Partner Portal's own pages and Internal Tools. The account card at the foot
// opens Settings, which slides up over the menu with a Back row.

const NAV_DEFAULT_WIDTH = 256;
const NAV_MIN_WIDTH = 220;
const NAV_MAX_WIDTH = 380;
const NAV_WIDTH_KEY = "partners:nav-width";
const NAV_KEY_STEP = 16;
const HOVER_INTENT_MS = 150;
const DOCKED_KEY = "partners:nav-docked";
const clampNav = (w: number) => Math.round(Math.min(NAV_MAX_WIDTH, Math.max(NAV_MIN_WIDTH, w)));

const row =
  "flex h-9 w-full min-w-0 shrink-0 cursor-pointer items-center gap-3 rounded-lg px-[10px] text-left text-[15px] font-normal text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const label =
  "min-w-0 flex-1 truncate opacity-0 transition-opacity duration-(--motion-base) group-data-[open]:opacity-100 group-data-[open]:delay-75";
const icon = "h-5 w-5 shrink-0";
// The current page: a soft square behind the icon in the rail, the Signal wash pill once open.
const current = "font-medium text-foreground group-data-[open]:bg-primary/[0.13]";
const iconWell = "-mx-2 grid size-9 shrink-0 place-items-center rounded-lg";
const currentWell = "bg-foreground/[0.06] group-data-[open]:bg-transparent";

/** The suite, in the Dashboard's order; `key` is the Dashboard's `/open/<key>`. */
const PRODUCTS: { key: string; name: string; icon: LucideIcon }[] = [
  { key: "agent-portal", name: "Partner Portal", icon: UsersRound },
  { key: "issuing", name: "Issuing", icon: CreditCard },
  { key: "gateway", name: "Gateway", icon: ArrowLeftRight },
  { key: "gifts", name: "Gifts", icon: Gift },
  { key: "neural", name: "Neural", icon: Sparkles },
];
const CURRENT_PRODUCT = "agent-portal";

function storedFlag(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}

function useStoredFlag(key: string, fallback: boolean): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState(() => storedFlag(key, fallback));
  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        localStorage.setItem(key, next ? "1" : "0");
      } catch {
        // Private mode: the choice just won't survive a reload.
      }
    },
    [key],
  );
  return [value, set];
}

function storedNavWidth(): number {
  try {
    const n = Number(localStorage.getItem(NAV_WIDTH_KEY));
    return n ? clampNav(n) : NAV_DEFAULT_WIDTH;
  } catch {
    return NAV_DEFAULT_WIDTH;
  }
}

// The panel opens on focus only from the keyboard: a click focuses the link too,
// and that must not hold the panel open after the pointer leaves.
let lastInput: "keyboard" | "pointer" = "pointer";
if (typeof document !== "undefined") {
  document.addEventListener("keydown", () => (lastInput = "keyboard"), true);
  document.addEventListener("pointerdown", () => (lastInput = "pointer"), true);
}

/** Where Back from Settings returns to. */
let lastAppPath = "/";
const isSettingsPath = (pathname: string) => pathname === "/settings" || pathname.startsWith("/settings/");

const startsWith = (pathname: string, base: string) => pathname === base || pathname.startsWith(`${base}/`);

/* ── Rows ── */

function NavRow({
  to,
  icon: Icon,
  children,
  current: isCurrent,
  badge = 0,
}: {
  to: string;
  icon: LucideIcon;
  children: ReactNode;
  current: boolean;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      aria-current={isCurrent ? "page" : undefined}
      aria-label={badge > 0 && typeof children === "string" ? `${children}, ${badge} unread` : undefined}
      className={cn(row, "relative", isCurrent && current)}
    >
      <span className={cn(iconWell, isCurrent && currentWell)}>
        <Icon className={icon} strokeWidth={1.5} aria-hidden="true" />
      </span>
      <CountBadge count={badge} className="absolute left-[21px] top-[2px]" />
      <span className={label}>{children}</span>
    </Link>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Current on this path and everything under it. */
  prefix?: boolean;
  also?: string[];
}

const isCurrentItem = (pathname: string, item: NavItem) =>
  (item.prefix && item.to !== "/" ? startsWith(pathname, item.to) : pathname === item.to) ||
  (item.also?.some((p) => startsWith(pathname, p)) ?? false);

/** A collapsible set of rows (Internal Tools, Mailroom), its open state remembered. */
function NavGroup({
  storageKey,
  title,
  icon: Icon,
  items,
  holds = [],
  pathname,
  children,
}: {
  storageKey: string;
  title: string;
  icon: LucideIcon;
  items: NavItem[];
  /** Rows nested deeper whose page also holds this group open. */
  holds?: NavItem[];
  pathname: string;
  children?: ReactNode;
}) {
  const [savedOpen, setSavedOpen] = useStoredFlag(storageKey, true);
  const open = savedOpen || [...items, ...holds].some((item) => isCurrentItem(pathname, item));
  return (
    <>
      <button
        type="button"
        onClick={() => setSavedOpen(!open)}
        aria-expanded={open}
        aria-label={`${open ? "Collapse" : "Expand"} ${title}`}
        className={row}
      >
        <Icon className={icon} strokeWidth={1.5} aria-hidden="true" />
        <span className={label}>{title}</span>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center opacity-0 transition-opacity group-data-[open]:opacity-100">
          <NavCaret open={open} />
        </span>
      </button>
      <div
        data-expanded={open ? "" : undefined}
        inert={!open}
        className="grid shrink-0 grid-rows-[0fr] transition-[grid-template-rows] duration-(--motion-base) ease-standard data-[expanded]:grid-rows-[1fr] motion-reduce:transition-none"
      >
        <div className="flex min-h-0 flex-col gap-0.5 overflow-hidden">
          {items.map((item) => (
            <NavRow key={item.to} to={item.to} icon={item.icon} current={isCurrentItem(pathname, item)}>
              {item.label}
            </NavRow>
          ))}
          {children}
        </div>
      </div>
    </>
  );
}

/** A small solid triangle, ▾ closed and ▴ open. */
function NavCaret({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 10 10"
      aria-hidden="true"
      className={cn("h-2.5 w-2.5 shrink-0 transition-transform duration-(--motion-fast) motion-reduce:transition-none", open && "rotate-180")}
    >
      <path d="M1.5 3.25h7L5 7.25z" fill="currentColor" />
    </svg>
  );
}

/* ── Product switcher: the lockup opens it ── */

// The rail's switcher is open, so the hover panel underneath doesn't fold away from it.
const useSwitcher = create<{ open: boolean; setOpen: (open: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/**
 * The lockup as the way between products. Another product opens through the Dashboard, which
 * decides how; this one is marked current. People with no other products (GoDaddy merchants) get
 * the plain link home instead.
 *
 * Each switcher keeps its own open state: the phone bar is always mounted (only CSS hides it), and
 * a shared flag opened its menu too, at the top-left under the cursor, so the click picked "Dashboard".
 */
function ProductSwitcher({
  className,
  pinsMenu = false,
  children,
}: {
  className?: string;
  pinsMenu?: boolean;
  children: (switches: boolean) => ReactNode;
}) {
  const { showProducts } = useNavItems();
  const staff = useInternalUi();
  const [open, setOpenState] = useState(false);
  const pin = useSwitcher((st) => st.setOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    if (pinsMenu) pin(next);
  };
  const base = dashboardUrl();
  if (!showProducts) {
    return (
      <a href={`${base}/`} aria-label="Myela dashboard" className={className}>
        {children(false)}
      </a>
    );
  }
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Switch product" className={cn("cursor-pointer text-left", className)}>
          {children(true)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="w-56">
        <DropdownMenuItem asChild>
          <a href={`${base}/`}>
            <LayoutGrid aria-hidden="true" />
            Dashboard
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {PRODUCTS.map((p) => {
          const Icon = p.icon;
          const here = p.key === CURRENT_PRODUCT;
          return (
            <DropdownMenuItem key={p.key} asChild>
              <a href={here ? undefined : `${base}/open/${p.key}`} aria-current={here ? "true" : undefined}>
                <Icon aria-hidden="true" />
                <span className="flex-1">{p.name}</span>
                {here && <Check className="text-muted-foreground" aria-label="You're here" />}
              </a>
            </DropdownMenuItem>
          );
        })}
        {staff && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/merchant?pick=1">
                <Store aria-hidden="true" />
                <span className="flex-1">Merchant portal</span>
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The up/down selector mark that says the lockup opens something; only where it does. */
function SwitcherCaret() {
  return <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} aria-hidden="true" />;
}

/** The standard unread badge: a red pill with a white number, hidden at zero. */
function CountBadge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--negative-600)] px-1 text-[10px] font-semibold leading-none text-white",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function initialsOf(user: { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined) {
  return `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`.toUpperCase() || user?.email?.[0]?.toUpperCase() || "?";
}

function AccountCard({ onOpen, buttonRef }: { onOpen: () => void; buttonRef?: Ref<HTMLButtonElement> }) {
  const { user } = useAuth();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onOpen}
      aria-label={`Settings for ${fullName || user?.email}`}
      className="flex h-12 w-full shrink-0 cursor-pointer items-center gap-3 rounded-lg pl-[14px] pr-[11px] transition hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="grid size-[26px] shrink-0 place-items-center rounded-lg bg-muted text-[11px] font-semibold text-foreground">
        {initialsOf(user)}
      </span>
      <span className="grid min-w-0 flex-1 text-left leading-tight opacity-0 transition-opacity duration-(--motion-base) group-data-[open]:opacity-100">
        <span className="truncate text-sm text-foreground">{fullName || user?.email}</span>
        {fullName ? <span className="truncate text-xs text-muted-foreground">{user?.email}</span> : null}
      </span>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-(--motion-base) group-data-[open]:opacity-100"
        aria-hidden="true"
      />
    </button>
  );
}

/* ── Notifications: the bell in the header, the list sliding up over the menu ── */

const useNotifLayer = create<{ open: boolean; setOpen: (open: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

function NavBell({ className }: { className?: string }) {
  const unread = useUnreadAlertCount();
  const open = useNotifLayer((st) => st.open);
  const setOpen = useNotifLayer((st) => st.setOpen);
  // Notifications have no row in the menu, so the bell is where "you are here" shows: the same
  // Signal wash as the current page's row, while the list is open or on All notifications.
  const onPage = useLocation().pathname === "/notifications";
  const here = open || onPage;
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      aria-expanded={open}
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
      title="Notifications"
      className={cn(
        "relative flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        here ? "bg-primary/[0.13] text-foreground" : "text-muted-foreground hover:bg-foreground/10",
        className,
      )}
    >
      <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
      <CountBadge count={unread} className="absolute -right-0.5 -top-0.5" />
    </button>
  );
}

function NotificationRow({ alert, onOpen }: { alert: UserAlert; onOpen: (alert: UserAlert) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(alert)}
        className="flex w-full cursor-pointer flex-col gap-1 rounded-lg px-[10px] py-2 text-left transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-2">
          {!alert.is_read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
          <span className={cn("min-w-0 flex-1 truncate text-sm font-medium", alert.is_read && "text-muted-foreground")}>{alert.title}</span>
        </span>
        <span className="line-clamp-2 text-xs text-muted-foreground">{alert.message}</span>
        <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(alert.created_date), { addSuffix: true })}</span>
      </button>
    </li>
  );
}

function NotificationsLayer() {
  const open = useNotifLayer((st) => st.open);
  const setOpen = useNotifLayer((st) => st.setOpen);
  const unread = useUnreadAlertCount();
  const { data: alerts, isLoading } = useUserAlerts(true);
  const markRead = useMarkAlertRead();
  const markAllRead = useMarkAllAlertsRead();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  useEffect(() => setOpen(false), [pathname, setOpen]);

  const openItem = (alert: UserAlert) => {
    if (!alert.is_read) markRead.mutate(alert.id);
    const link = buildAlertLink(alert);
    if (link) navigate(link);
    else toast.error("This notification has no destination yet.");
  };

  // No title row: the lit bell already says where you are. The list lines up with the menu's rows
  // (15px column + 10px row inset), and the bell or Escape closes it, so there is no Back row.
  // It fades with a slight rise rather than sliding the full height: opening "All notifications"
  // or a notification closes it while the page underneath changes, and only the page should move.
  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-hidden={!open}
      inert={!open}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        setOpen(false);
      }}
      className={cn(
        "absolute inset-x-0 bottom-0 top-14 z-10 flex flex-col bg-background transition-[opacity,translate] duration-(--motion-base) ease-standard motion-reduce:transition-none",
        open ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0",
      )}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-[15px] py-2 opacity-0 transition-opacity duration-(--motion-base) group-data-[open]:opacity-100">
        {isLoading ? null : alerts && alerts.length > 0 ? (
          <ul className="flex flex-col gap-0.5">
            {alerts.map((alert) => (
              <NotificationRow key={alert.id} alert={alert} onOpen={openItem} />
            ))}
          </ul>
        ) : (
          <p className="px-[10px] py-2 text-sm text-muted-foreground">No notifications yet.</p>
        )}
      </div>
      <div className="flex shrink-0 flex-col gap-0.5 border-t border-foreground/10 px-[15px] py-2">
        {unread > 0 && (
          <button type="button" onClick={() => markAllRead.mutate()} className={row}>
            <CheckCheck className={icon} strokeWidth={1.5} aria-hidden="true" />
            <span className={label}>Mark all read</span>
          </button>
        )}
        <Link to="/notifications" className={row}>
          <Bell className={icon} strokeWidth={1.5} aria-hidden="true" />
          <span className={label}>All notifications</span>
        </Link>
      </div>
    </div>
  );
}

/* ── Settings takeover ── */

function SettingsLayer({ open, onBack, backRef }: { open: boolean; onBack: () => void; backRef?: Ref<HTMLButtonElement> }) {
  const { user, signOut, isImpersonating } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const canUseIntegrations = user?.userType === "INTERNAL" && !isImpersonating;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
      toast.success("Successfully signed out");
    } catch {
      toast.error("Failed to sign out");
    }
  };

  return (
    <div
      role="region"
      aria-label="Settings"
      aria-hidden={!open}
      inert={!open}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== "Escape" || !e.currentTarget.contains(e.target as Node)) return;
        e.stopPropagation();
        onBack();
      }}
      className={cn(
        "absolute inset-x-0 bottom-0 top-14 z-10 flex flex-col bg-background transition-transform duration-(--motion-base) ease-standard motion-reduce:transition-none",
        open ? "translate-y-0" : "translate-y-full",
      )}
    >
      <div className="flex h-12 shrink-0 items-center pl-[25px] pr-[15px]">
        <h2 className="min-w-0 flex-1 truncate text-[15px] font-medium text-foreground opacity-0 transition-opacity duration-(--motion-base) group-data-[open]:opacity-100 group-data-[open]:delay-75">
          Settings
        </h2>
      </div>
      <nav aria-label="Settings pages" className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden px-[15px] py-2">
        <NavRow to="/settings/profile" icon={UserRound} current={pathname === "/settings/profile"}>
          Profile
        </NavRow>
        <NavRow to="/settings/notifications" icon={Settings} current={pathname === "/settings/notifications"}>
          Notification settings
        </NavRow>
        {canUseIntegrations ? (
          <NavRow to="/settings/integrations" icon={Plug} current={pathname === "/settings/integrations"}>
            Integrations
          </NavRow>
        ) : null}
      </nav>
      <div className="flex shrink-0 flex-col gap-0.5 border-t border-foreground/10 px-[15px] py-2">
        <button type="button" onClick={toggleTheme} className={row}>
          {theme === "light" ? <Moon className={icon} strokeWidth={1.5} aria-hidden="true" /> : <Sun className={icon} strokeWidth={1.5} aria-hidden="true" />}
          <span className={label}>{theme === "light" ? "Dark mode" : "Light mode"}</span>
        </button>
        <button type="button" onClick={handleSignOut} className={cn(row, "hover:!text-destructive")}>
          <LogOut className={icon} strokeWidth={1.5} aria-hidden="true" />
          <span className={label}>Sign out</span>
        </button>
      </div>
      <div className="shrink-0 border-t border-foreground/10 px-[15px] py-2">
        <button ref={backRef} type="button" onClick={onBack} aria-label="Back to menu from Settings" className={cn(row, "text-foreground")}>
          <ArrowLeft className={icon} strokeWidth={1.5} aria-hidden="true" />
          <span className={cn(label, "font-medium")}>Back</span>
        </button>
      </div>
    </div>
  );
}

/* ── What the menu lists, by who is looking ── */

function useNavItems() {
  const { user } = useAuth();
  // While impersonating, the menu shows what the impersonated user sees; roles
  // aren't carried on that payload, so admin-gated items stay hidden.
  const effective = user?.impersonating ?? user;
  const userType = effective?.userType;
  const isInternal = userType === "INTERNAL";
  const roles: string[] = user?.impersonating ? [] : (user?.roles ?? []);

  if (userType === "GODADDY_MERCHANT") {
    return {
      main: [
        { to: "/poynt-collect", label: "Poynt Collect", icon: CreditCard },
        { to: "/helpdesk", label: "Helpdesk", icon: HelpCircle, prefix: true },
      ] as NavItem[],
      tools: [] as NavItem[],
      mailroom: [] as NavItem[],
      showProducts: false,
    };
  }

  const main: NavItem[] = [
    { to: "/", label: "Home", icon: House },
    { to: "/applications", label: isInternal ? "Applications" : "My Applications", icon: FileText, prefix: true, also: ["/new-application"] },
    { to: "/merchants", label: isInternal ? "Merchants" : "My Merchants", icon: Store, prefix: true },
    ...(userType === "AGENT" && AGENT_STATEMENTS_ENABLED ? [{ to: "/commissions", label: "My Commissions", icon: DollarSign, prefix: true }] : []),
    { to: "/products", label: productLabel("Products"), icon: Package },
    { to: "/documents", label: "Documents", icon: FolderOpen, prefix: true },
    ...(isInternal ? [{ to: "/poynt-collect", label: "Poynt Collect", icon: CreditCard }] : []),
    { to: "/helpdesk", label: "Helpdesk", icon: HelpCircle, prefix: true },
  ];

  const tools: NavItem[] = isInternal
    ? [
        { to: "/internal-tools/forms", label: "Forms", icon: ClipboardList, prefix: true },
        { to: "/admin/announcements", label: "Announcements", icon: Megaphone, prefix: true },
        { to: "/ai-call-log", label: "AI Call Log", icon: PhoneCall },
        { to: "/admin/document-templates", label: "DocuSeal Templates", icon: FileSignature, prefix: true },
        ...(roles.includes("admin") ? [{ to: "/admin/users", label: "Users", icon: Users }] : []),
        ...(canReadCommissions(roles) ? [{ to: "/admin/commissions", label: "Commissions", icon: DollarSign, prefix: true }] : []),
      ]
    : [];

  const mailroom: NavItem[] = isInternal
    ? [
        { to: "/mailroom", label: "Templates", icon: FileText },
        { to: "/mailroom/contacts", label: "Contacts", icon: Contact, prefix: true },
        { to: "/mailroom/groups", label: "Groups", icon: UsersRound, prefix: true },
        { to: "/mailroom/logs", label: "Logs", icon: ScrollText, prefix: true },
      ]
    : [];

  return { main, tools, mailroom, showProducts: true };
}

/* ── Body: shared by the desktop rail/panel and the phone drawer ── */

function NavBody() {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { main, tools, mailroom } = useNavItems();
  const inSettings = isSettingsPath(pathname);
  const notifOpen = useNotifLayer((st) => st.open);
  const covered = inSettings || notifOpen;

  useEffect(() => {
    if (!inSettings) lastAppPath = `${pathname}${location.search}`;
  }, [inSettings, pathname, location.search]);

  const accountRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<(() => HTMLElement | null) | null>(null);
  useEffect(() => {
    const target = focusNext.current;
    focusNext.current = null;
    target?.()?.focus({ preventScroll: true });
  }, [inSettings]);

  const openSettings = () => {
    focusNext.current = () => backRef.current;
    navigate("/settings/profile");
  };
  const closeSettings = () => {
    focusNext.current = () => accountRef.current;
    navigate(lastAppPath);
  };

  return (
    // `contents`: the settings and notification layers position against the panel column.
    <div className="contents">
      <div className="flex min-h-0 flex-1 flex-col" inert={covered} aria-hidden={covered}>
        <nav aria-label="Primary" className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden px-[15px] pb-1">
          {main.map((item) => (
            <NavRow key={item.to} to={item.to} icon={item.icon} current={isCurrentItem(pathname, item)}>
              {item.label}
            </NavRow>
          ))}
          {tools.length > 0 ? (
            <>
              <div aria-hidden="true" className="-mx-[15px] my-2 h-px shrink-0 bg-foreground/10" />
              <NavGroup storageKey="partners:nav-tools-open" title="Internal Tools" icon={Wrench} items={tools} holds={mailroom} pathname={pathname}>
                <NavGroup storageKey="partners:nav-mailroom-open" title="Mailroom" icon={Mail} items={mailroom} pathname={pathname} />
              </NavGroup>
            </>
          ) : null}
        </nav>
        <div className="shrink-0 border-t border-foreground/10 px-2 py-2">
          <AccountCard onOpen={openSettings} buttonRef={accountRef} />
        </div>
      </div>
      <NotificationsLayer />
      <SettingsLayer open={inSettings} onBack={closeSettings} backRef={backRef} />
    </div>
  );
}

/** In the rail, the unread count rides on the mark. */
function RailUnread() {
  const unread = useUnreadAlertCount();
  return <CountBadge count={unread} className="absolute left-[42px] top-[12px]" />;
}

/** The open menu's right edge: drag to resize, arrow keys when focused, double-click to reset. */
function NavResizeHandle({ width, onWidth, onResizingChange }: { width: number; onWidth: (w: number) => void; onResizingChange: (on: boolean) => void }) {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);
  const end = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    onResizingChange(false);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize menu"
      aria-valuemin={NAV_MIN_WIDTH}
      aria-valuemax={NAV_MAX_WIDTH}
      aria-valuenow={width}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        drag.current = { startX: e.clientX, startWidth: width };
        e.currentTarget.setPointerCapture(e.pointerId);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        onResizingChange(true);
      }}
      onPointerMove={(e) => {
        if (drag.current) onWidth(clampNav(drag.current.startWidth + (e.clientX - drag.current.startX)));
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => onWidth(NAV_DEFAULT_WIDTH)}
      onKeyDown={(e) => {
        const next = e.key === "ArrowRight" ? width + NAV_KEY_STEP : e.key === "ArrowLeft" ? width - NAV_KEY_STEP : null;
        if (next === null) return;
        e.preventDefault();
        onWidth(clampNav(next));
      }}
      // Below the header seam, so the collapse tab stays clickable.
      className="group/resize absolute bottom-0 right-0 top-[76px] z-50 w-2 translate-x-1/2 cursor-col-resize touch-none focus-visible:outline-none"
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 transition-colors group-hover/resize:bg-primary/60 group-focus-visible/resize:bg-primary group-active/resize:bg-primary"
      />
    </div>
  );
}

/* ── Desktop: rail + hover panel ── */

function useFlyout(docked: boolean) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // A pick from the panel closes it, unless the pointer is still over it.
  const pathname = useLocation().pathname;
  // Reads the DOM's :hover, so it has to run after the navigation commits.
  useEffect(() => {
    if (rootRef.current?.matches(":hover")) return;
    window.clearTimeout(timer.current);
    setHover(false);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (lastInput !== "keyboard") setFocus(false);
  }, [pathname]);

  const close = useCallback(() => {
    window.clearTimeout(timer.current);
    setHover(false);
    setFocus(false);
  }, []);

  const handlers = {
    onPointerEnter: () => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setHover(true), HOVER_INTENT_MS);
    },
    onPointerLeave: () => {
      window.clearTimeout(timer.current);
      setHover(false);
    },
    onFocus: (e: FocusEvent<HTMLElement>) => {
      if (lastInput !== "keyboard") return;
      if (!rootRef.current?.contains(e.relatedTarget as Node | null)) returnFocus.current = e.relatedTarget as HTMLElement | null;
      setFocus(true);
    },
    onBlur: (e: FocusEvent<HTMLElement>) => {
      if (rootRef.current?.contains(e.relatedTarget as Node | null)) return;
      setFocus(false);
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== "Escape" || docked) return;
      close();
      const back = returnFocus.current;
      if (back?.isConnected) back.focus();
      else (document.activeElement as HTMLElement | null)?.blur();
    },
  };

  const notifOpen = useNotifLayer((st) => st.open);
  const switching = useSwitcher((st) => st.open);
  const pinned = notifOpen || switching;
  return { rootRef, open: docked || hover || focus || pinned, close, handlers };
}

export function PrimaryNav() {
  const [docked, setDocked] = useStoredFlag(DOCKED_KEY, false);
  const { rootRef, open, close, handlers } = useFlyout(docked);
  const [navWidth, setNavWidthState] = useState(storedNavWidth);
  const [resizing, setResizing] = useState(false);
  const setNavWidth = (w: number) => {
    setNavWidthState(w);
    try {
      localStorage.setItem(NAV_WIDTH_KEY, String(w));
    } catch {
      // Private mode: the width just won't survive a reload.
    }
  };

  return (
    // Hover is tracked on this wrapper, not the aside, so reaching for the edge tab doesn't fold the panel away.
    <div
      ref={rootRef}
      {...handlers}
      style={{ "--nav-w": `${navWidth}px` } as CSSProperties}
      className={cn(
        "group/nav relative hidden shrink-0 transition-[width] duration-(--motion-base) ease-standard motion-reduce:transition-none md:block",
        docked ? "w-[var(--nav-w)]" : "w-[70px]",
        resizing && "!transition-none",
      )}
    >
      <aside
        data-open={open ? "" : undefined}
        // The aside clips a wider column; never let focus scroll it sideways or down onto the layers.
        onScroll={(e) => {
          if (e.currentTarget.scrollLeft) e.currentTarget.scrollLeft = 0;
          if (e.currentTarget.scrollTop) e.currentTarget.scrollTop = 0;
        }}
        className={cn(
          "group absolute inset-y-0 left-0 z-40 flex w-[70px] flex-col overflow-hidden border-r border-foreground/10 bg-background text-foreground transition-[width,box-shadow] duration-(--motion-base) ease-standard data-[open]:w-[var(--nav-w)] motion-reduce:transition-none",
          open && !docked && "shadow-[var(--shadow-lg)]",
          resizing && "!transition-none",
        )}
      >
        <div className="relative flex h-full w-[calc(var(--nav-w)-1px)] flex-col">
          <div className="relative flex h-14 shrink-0 items-center">
            <ProductSwitcher pinsMenu className="relative block h-full flex-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
              {(switches) => (
                <>
                  <span className="absolute inset-y-0 left-0 flex w-[69px] items-center justify-center text-foreground transition-opacity duration-(--motion-fast) group-data-[open]:opacity-0">
                    <span aria-hidden="true">
                      <MyelaMark size={26} />
                    </span>
                    <RailUnread />
                  </span>
                  <span className="absolute inset-y-0 left-[25px] flex items-center gap-1.5 text-foreground opacity-0 transition-opacity duration-(--motion-base) group-data-[open]:opacity-100 group-data-[open]:delay-75">
                    <Lockup size={15} title="Myela" />
                    {switches && <SwitcherCaret />}
                  </span>
                </>
              )}
            </ProductSwitcher>
            <NavBell className="mr-3 opacity-0 group-data-[open]:opacity-100" />
          </div>
          <NavBody />
        </div>
      </aside>
      {open ? (
        <div className="pointer-events-none absolute inset-y-0 left-0 z-[42]" style={{ width: navWidth }}>
          <span className="pointer-events-auto">
            <NavResizeHandle width={navWidth} onWidth={setNavWidth} onResizingChange={setResizing} />
          </span>
        </div>
      ) : null}
      {/* Collapse / keep open: a small tab on the menu's edge, at the header's seam. */}
      <button
        type="button"
        onClick={() => {
          if (docked) close();
          setDocked(!docked);
        }}
        aria-pressed={docked}
        aria-label={docked ? "Collapse menu" : "Keep menu open"}
        title={docked ? "Collapse menu" : "Keep menu open"}
        className={cn(
          "absolute top-[45px] z-[41] flex h-[22px] w-[22px] -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-foreground/10 bg-background text-muted-foreground opacity-0 shadow-[var(--shadow-sm)] transition-[left,opacity,color] duration-(--motion-base) ease-standard hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/nav:opacity-100 motion-reduce:transition-none",
          open ? "left-[var(--nav-w)]" : "left-[70px]",
          resizing && "!transition-none",
        )}
      >
        {docked ? <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
      </button>
    </div>
  );
}

/* ── Phone: a top bar and a drawer holding the same body, always open ── */

export function MobileBar() {
  const pathname = useLocation().pathname;
  // Open on the page it was opened from; any navigation closes it.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = openOn === pathname;
  const setOpen = (next: boolean) => setOpenOn(next ? pathname : null);

  return (
    <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4 md:hidden">
      <ProductSwitcher className="flex items-center gap-1.5 text-foreground">
        {(switches) => (
          <>
            <Lockup size={15} title="Myela" />
            {switches && <SwitcherCaret />}
          </>
        )}
      </ProductSwitcher>
      <Sheet open={open} onOpenChange={setOpen}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        <SheetContent side="left" aria-describedby={undefined} className="w-[280px] gap-0 p-0 sm:max-w-[280px]">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div data-open="" className="group relative flex h-full flex-col overflow-hidden">
            <div className="flex h-14 shrink-0 items-center justify-between pl-[25px] pr-12 text-foreground">
              <Lockup size={15} title="Myela" />
              <NavBell />
            </div>
            <NavBody />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
